import React from 'react';
import { Star, MapPin } from 'lucide-react';
import { avisPourVille, COLOR_MAP, GOOGLE_REVIEWS_URL, NOTE_GOOGLE, NB_AVIS_GOOGLE } from '../data/avis';
import { getVille, villesProches } from '../data/villes';

const GoogleG = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-label="Avis Google">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

/** Avis Google affichés sur une page ville : avis de la commune si disponibles, sinon avis des environs. */
const AvisVille = ({ slug, nom }) => {
  const ville = getVille(slug) || { slug, nom };
  const { avis, local } = avisPourVille(ville, villesProches(ville, 12));
  if (avis.length === 0) return null;

  return (
    <section className="py-12 md:py-16 px-4 bg-white" data-testid="avis-ville-section">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
          <div>
            <p className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-1">Avis Google</p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900" data-testid="avis-ville-title">
              {local ? `Ils nous ont fait confiance à ${nom}` : `Ce que disent nos clients autour de ${nom}`}
            </h2>
          </div>
          <div className="flex items-center gap-2 text-gray-700">
            <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
            <span className="font-bold text-gray-900">{NOTE_GOOGLE.replace(".0", "")}/5</span>
            <span className="text-gray-500 text-sm">• {NB_AVIS_GOOGLE} avis Google</span>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {avis.map((a) => {
            const villeAvis = a.ville ? getVille(a.ville) : null;
            return (
              <div key={a.name + a.text.slice(0, 12)} className="bg-gray-50 rounded-2xl p-5 border border-gray-100" data-testid="avis-ville-card">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-0.5">
                    {[...Array(a.rating)].map((_, i) => <Star key={`${a.name}-${i}`} className="w-4 h-4 fill-yellow-400 text-yellow-400" />)}
                  </div>
                  <GoogleG />
                </div>
                <p className="text-gray-700 italic mb-4">« {a.text} »</p>
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold ${COLOR_MAP[a.color]}`}>{a.initial}</div>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">{a.name}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      {villeAvis && <><MapPin className="w-3 h-3" />{villeAvis.nom} • </>}{a.date}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 text-center">
          <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" data-testid="avis-ville-google-link" className="inline-flex items-center gap-2 text-gray-800 border-2 border-gray-200 hover:border-gray-400 px-6 py-3 rounded-xl font-semibold transition-colors">
            <GoogleG /> Voir tous nos avis Google
          </a>
        </div>
      </div>
    </section>
  );
};

export default AvisVille;
